import React from 'react';
import {
  Gauge,
  Clock,
  Shield,
  AlertTriangle,
  BarChart3,
  MessageSquare,
  Palette,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import PresentationInsightsCharts, { Top3IssueCategories } from './PresentationInsightsCharts';
import { UnifiedPriorityList } from './UnifiedPriorityList';
import { simplifyMetricLabel } from '../utils/languageSimplifier';

// Metric Card Component
const MetricCard = ({ icon: Icon, label, primary, secondary, trend, expertiseLevel }) => (
  <Card className="group hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-card to-card/80 border-border/50 hover:border-accent/30">
    <CardHeader className="pb-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            trend === 'up'
              ? 'bg-green-500/10 text-green-600 dark:text-green-400'
              : trend === 'down'
              ? 'bg-red-500/10 text-red-600 dark:text-red-400'
              : 'bg-accent/10 text-accent'
          }`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="text-sm font-medium text-muted-foreground">{simplifyMetricLabel(label, expertiseLevel)}</div>
        </div>
      </div>
    </CardHeader>
    <CardContent>
      <div className="space-y-2">
        <div className="text-3xl font-bold text-foreground group-hover:text-accent transition-colors">
          {primary}
        </div>
        {secondary && (
          <p className="text-sm text-muted-foreground">{secondary}</p>
        )}
      </div>
    </CardContent>
  </Card>
);

export function OverviewTab({ analysis, analysisFocus, expertiseLevel = 'beginner', projectId }) {
  const slides = analysis?.slides || [];
  const slidesCount = slides.length;

  // Determine analysis type (fallback to 'both' if not specified)
  const analysisType = analysisFocus || analysis?.analysisType || 'both';

  // Calculate metrics based on analysis type
  const calculateMetrics = () => {
    const unified = analysis?.unified_analysis?.unified_metrics;

    // Common metrics for all types
    const estimatedTime = analysis?.estimatedViewTime || `${Math.ceil(slidesCount * 1.5)}m`;
    const brandComplianceScore = analysis?.brandCompliance?.score || 'N/A';
    const hasBrandGuidelines = analysis?.brandCompliance?.hasGuidelines || false;

    // Count slides needing edits
    const slidesNeedingEdits = slides.filter(slide => {
      const recs = slide.recommendations;
      const hasContentIssues = recs?.content_refinements?.length > 0;
      const hasDesignIssues = recs?.visual_content?.length > 0;
      return hasContentIssues || hasDesignIssues;
    }).length;

    // Get top issue categories
    const issueCategories = {};
    slides.forEach(slide => {
      const recs = slide.recommendations;
      if (recs?.content_refinements) {
        recs.content_refinements.forEach(rec => {
          // Extract category from recommendation text (simple heuristic)
          if (rec.toLowerCase().includes('headline')) issueCategories['Headlines'] = (issueCategories['Headlines'] || 0) + 1;
          else if (rec.toLowerCase().includes('bullet') || rec.toLowerCase().includes('text')) issueCategories['Text Density'] = (issueCategories['Text Density'] || 0) + 1;
          else if (rec.toLowerCase().includes('flow') || rec.toLowerCase().includes('transition')) issueCategories['Flow'] = (issueCategories['Flow'] || 0) + 1;
          else issueCategories['Content'] = (issueCategories['Content'] || 0) + 1;
        });
      }
      if (recs?.visual_content) {
        recs.visual_content.forEach(rec => {
          if (rec.toLowerCase().includes('color')) issueCategories['Color'] = (issueCategories['Color'] || 0) + 1;
          else if (rec.toLowerCase().includes('layout') || rec.toLowerCase().includes('alignment')) issueCategories['Layout'] = (issueCategories['Layout'] || 0) + 1;
          else if (rec.toLowerCase().includes('typography') || rec.toLowerCase().includes('font')) issueCategories['Typography'] = (issueCategories['Typography'] || 0) + 1;
          else issueCategories['Visual'] = (issueCategories['Visual'] || 0) + 1;
        });
      }
    });

    const topIssue = Object.entries(issueCategories)
      .sort((a, b) => b[1] - a[1])[0];
    const topIssueText = topIssue ? `${topIssue[0]} (${topIssue[1]})` : 'None';

    if (analysisType === 'content') {
      // Content-only metrics
      const contentScore = unified?.content_clarity_avg || analysis?.presentationScore?.content || 75;
      const narrativeScore = unified?.strategic_alignment || analysis?.presentationScore?.narrative || 70;

      // Count grammatical errors (mock for now - would need actual grammar check)
      const grammaticalErrors = slides.reduce((sum, slide) => {
        // This is a placeholder - actual implementation would check for grammar issues
        return sum + (slide.text_content?.split('  ').length || 0); // Double spaces as proxy
      }, 0);

      return {
        metrics: [
          { icon: Gauge, label: 'Overall Content Score', primary: `${contentScore}/100`, secondary: 'Content quality', trend: contentScore >= 75 ? 'up' : 'down' },
          { icon: Clock, label: 'Est. Time to Present', primary: estimatedTime, secondary: 'Estimated duration' },
          { icon: Shield, label: 'Brand Compliance', primary: hasBrandGuidelines ? brandComplianceScore : 'N/A', secondary: hasBrandGuidelines ? '' : 'No guidelines uploaded' },
          { icon: AlertTriangle, label: 'Slides Need Edits', primary: `${slidesNeedingEdits}/${slidesCount}`, secondary: `${Math.round((slidesNeedingEdits/slidesCount)*100)}% of slides` },
          { icon: MessageSquare, label: 'Narrative Score', primary: `${narrativeScore}/100`, trend: narrativeScore >= 75 ? 'up' : 'down' },
          { icon: FileText, label: 'Grammatical Errors', primary: grammaticalErrors, secondary: grammaticalErrors === 0 ? 'Clean!' : 'Needs review' },
          { icon: BarChart3, label: 'Top Issue Category', primary: topIssueText, secondary: 'Most common' }
        ]
      };
    } else if (analysisType === 'design') {
      // Design-only metrics
      const designScore = unified?.design_quality_avg || analysis?.presentationScore?.design || 75;
      const dataVizScore = slides.reduce((sum, slide) => {
        // Check for charts/graphs in design quality
        const hasCharts = slide.design_quality?.has_charts || false;
        return hasCharts ? sum + (slide.design_quality?.chart_quality || 70) : sum;
      }, 0) / Math.max(1, slides.filter(s => s.design_quality?.has_charts).length) || 'N/A';

      return {
        metrics: [
          { icon: Gauge, label: 'Overall Design Score', primary: `${designScore}/100`, secondary: 'Visual quality', trend: designScore >= 75 ? 'up' : 'down' },
          { icon: Clock, label: 'Est. Time to Present', primary: estimatedTime, secondary: 'Estimated duration' },
          { icon: AlertTriangle, label: 'Slides Need Edits', primary: `${slidesNeedingEdits}/${slidesCount}`, secondary: `${Math.round((slidesNeedingEdits/slidesCount)*100)}% of slides` },
          { icon: BarChart3, label: 'Data Visualization', primary: typeof dataVizScore === 'number' ? `${Math.round(dataVizScore)}/100` : dataVizScore, secondary: typeof dataVizScore === 'number' ? 'Avg chart quality' : 'No charts detected' },
          { icon: Shield, label: 'Brand Compliance', primary: hasBrandGuidelines ? brandComplianceScore : 'N/A', secondary: hasBrandGuidelines ? '' : 'No guidelines uploaded' },
          { icon: Palette, label: 'Top Issue Category', primary: topIssueText, secondary: 'Most common' }
        ]
      };
    } else {
      // Both (content + design) metrics
      const overallScore = unified?.overall_score || analysis?.presentationScore?.overall || 75;

      return {
        metrics: [
          { icon: Gauge, label: 'Overall Score', primary: `${overallScore}/100`, secondary: 'Combined quality', trend: overallScore >= 75 ? 'up' : 'down' },
          { icon: Clock, label: 'Est. Time to Present', primary: estimatedTime, secondary: 'Estimated duration' },
          { icon: Shield, label: 'Brand Compliance', primary: hasBrandGuidelines ? brandComplianceScore : 'N/A', secondary: hasBrandGuidelines ? '' : 'No guidelines uploaded' },
          { icon: AlertTriangle, label: 'Slides Need Edits', primary: `${slidesNeedingEdits}/${slidesCount}`, secondary: `${Math.round((slidesNeedingEdits/slidesCount)*100)}% of slides` },
          { icon: BarChart3, label: 'Top Issue Category', primary: topIssueText, secondary: 'Most common' },
          { icon: TrendingUp, label: 'Engagement Potential', primary: `${unified?.engagement || 75}/100`, secondary: 'Audience engagement' }
        ]
      };
    }
  };

  const { metrics } = calculateMetrics();

  return (
    <div className="space-y-8">
      {/* Analysis Type Badge */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-foreground">Overview</h2>
        <Badge variant="secondary" className={`
          ${analysisType === 'content' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400' : ''}
          ${analysisType === 'design' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400' : ''}
          ${analysisType === 'both' ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' : ''}
        `}>
          {analysisType === 'content' && '📝 Content Analysis'}
          {analysisType === 'design' && '🎨 Design Analysis'}
          {analysisType === 'both' && '⚡ Complete Analysis'}
        </Badge>
      </div>

      {/* Executive Summary */}
      {(() => {
        const unified = analysis?.unified_analysis;
        const overallScore = unified?.unified_metrics?.overall_score || 75;
        const slides = analysis?.slides || [];

        // Calculate strengths - only show actual strengths
        const strengths = [];
        if (overallScore >= 75) strengths.push('Strong overall presentation quality');

        const highQualitySlides = slides.filter(s => (s.design_quality?.scores?.overall || 0) >= 80).length;
        if (highQualitySlides > slides.length * 0.5) {
          strengths.push(`${highQualitySlides} slides have excellent design`);
        } else if (highQualitySlides > 0) {
          strengths.push(`${highQualitySlides} slide${highQualitySlides !== 1 ? 's have' : ' has'} excellent design`);
        }

        // Only show "minimal issues" if MOST slides (>50%) truly have ≤2 issues
        const lowIssueSlides = slides.filter(s => {
          const contentIssues = s.recommendations?.content_refinements?.length || 0;
          const visualIssues = s.recommendations?.visual_content?.length || 0;
          return (contentIssues + visualIssues) <= 2;
        }).length;
        if (lowIssueSlides > slides.length * 0.5) {
          strengths.push(`${lowIssueSlides} slides have minimal issues`);
        }

        // Check for specific design strengths
        const goodColorSlides = slides.filter(s => (s.design_quality?.scores?.color || 0) >= 80).length;
        if (goodColorSlides > slides.length * 0.6) {
          strengths.push('Consistent color scheme throughout');
        }

        const goodTypographySlides = slides.filter(s => (s.design_quality?.scores?.typography || 0) >= 80).length;
        if (goodTypographySlides > slides.length * 0.6) {
          strengths.push('Well-structured typography');
        }

        // Calculate focus areas
        const focusAreas = [];
        const priorities = unified?.unified_priorities || [];
        const highPriorities = priorities.filter(p => p.priority_level === 'high');

        if (highPriorities.length > 0) {
          const categories = {};
          highPriorities.forEach(p => {
            const cat = p.category || 'Other';
            categories[cat] = (categories[cat] || 0) + 1;
          });
          const topCategory = Object.entries(categories).sort((a, b) => b[1] - a[1])[0];
          if (topCategory) {
            focusAreas.push(`Address ${topCategory[1]} ${topCategory[0]} issue(s)`);
          }
        }

        const slidesNeedingWork = slides.filter(s => (s.design_quality?.scores?.overall || 0) < 60).length;
        if (slidesNeedingWork > 0) {
          focusAreas.push(`Improve ${slidesNeedingWork} slide(s) with low scores`);
        }

        if (focusAreas.length === 0) {
          focusAreas.push('Minor refinements and polish');
        }

        // Generate comprehensive summary
        const designAvg = unified?.unified_metrics?.design_quality_avg || 0;
        const contentAvg = unified?.unified_metrics?.content_clarity_avg || 0;
        const totalPriorities = unified?.unified_priorities?.length || 0;
        const highPriorityCount = highPriorities.length;

        const summaryText = overallScore >= 80
          ? `Your presentation demonstrates strong quality with an overall score of ${overallScore}/100. Analysis shows ${highQualitySlides} slide${highQualitySlides !== 1 ? 's' : ''} with excellent design quality (80+), and your content structure is well-organized. The design quality averages ${designAvg}/100, while content clarity sits at ${contentAvg}/100. With ${totalPriorities} total action items identified, ${highPriorityCount} are high-priority improvements that could elevate your presentation from good to exceptional. Your narrative flow is strong, and the visual hierarchy effectively guides viewers through your message.`
          : overallScore >= 60
          ? `Your presentation shows solid potential with a score of ${overallScore}/100. Analysis reveals ${slidesNeedingWork || 'several'} slide${slidesNeedingWork !== 1 ? 's that need' : ' that needs'} refinement to reach its full impact. Design quality averages ${designAvg}/100, and content clarity scores ${contentAvg}/100. We've identified ${totalPriorities} action items, with ${highPriorityCount} high-priority fixes that will significantly improve audience engagement. The foundation is there - you have ${highQualitySlides} strong slide${highQualitySlides !== 1 ? 's' : ''} to build from. Focus on consistency, visual hierarchy, and tightening your messaging to elevate the overall quality.`
          : `Your presentation scores ${overallScore}/100 and requires focused improvements to meet professional standards. With design quality at ${designAvg}/100 and content clarity at ${contentAvg}/100, there are ${slidesNeedingWork} slide${slidesNeedingWork !== 1 ? 's' : ''} needing significant work. We've identified ${totalPriorities} total issues, including ${highPriorityCount} high-priority items that are critical to address. The good news: every identified issue is actionable, and improving the high-priority items alone will dramatically increase your presentation's effectiveness. Start with the checklist below, prioritizing design consistency, content clarity, and visual hierarchy.`;

        return (
          <Card className="border-border/40 bg-gradient-to-br from-card/80 to-accent/5">
            <CardHeader>
              <CardTitle className="text-lg font-bold text-foreground">Executive Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Comprehensive Summary */}
              <div className="text-sm text-foreground leading-relaxed p-4 bg-gradient-to-r from-accent/10 to-transparent rounded-lg">
                {summaryText}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* What You Did Right */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                    <h3 className="font-semibold text-foreground">What You Did Right</h3>
                  </div>
                  <ul className="space-y-1.5">
                    {strengths.length > 0 ? (
                      strengths.map((strength, idx) => (
                        <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                          <span className="text-green-600 dark:text-green-400 mt-0.5">•</span>
                          <span>{strength}</span>
                        </li>
                      ))
                    ) : (
                      <li className="text-sm text-muted-foreground">Good foundation to build upon</li>
                    )}
                  </ul>
                </div>

                {/* What to Focus On */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                    <h3 className="font-semibold text-foreground">What to Focus On</h3>
                  </div>
                  <ul className="space-y-1.5">
                    {focusAreas.map((area, idx) => (
                      <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                        <span className="text-orange-600 dark:text-orange-400 mt-0.5">•</span>
                        <span>{area}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {metrics.map((metric, index) => (
          <MetricCard key={index} {...metric} expertiseLevel={expertiseLevel} />
        ))}
      </div>

      {/* Multidimensional Heat Map - Full Width */}
      <Card className="border-border/40 bg-card/60">
        <CardHeader>
          <CardTitle className="text-xl font-bold">Multi-Dimensional Heatmap</CardTitle>
          <p className="text-sm text-muted-foreground">Combined design + content quality per slide</p>
        </CardHeader>
        <CardContent>
          <PresentationInsightsCharts analysis={analysis} showOnlyHeatmap={true} />
        </CardContent>
      </Card>

      {/* The Checklist */}
      <UnifiedPriorityList analysis={analysis} expertiseLevel={expertiseLevel} projectId={projectId} />
    </div>
  );
}
